import React, { useState } from 'react';
import styled, { css } from 'styled-components';
import createLinkRel from '~/helpers/createLinkRel';
import { CTAIconPosition, CTATag } from './CTA.constants';
import UrlContext from '~/helpers/UrlContext';
import {
  CTAnavigationHeader,
  CTAnavigationFooter,
  CTAnavigationWithAniIcon,
  CTAnavigationItem,
} from './skins/CTA.navigation';
import {
  CTAiconOnly,
  CTAprimary,
  CTAprimaryWithAniIcon,
} from './skins/CTA.primary';
import { CTAsecondary, CTAsecondaryWithAniIcon } from './skins/CTA.secondary';
import {
  CTAtertiaryWithIcon,
  CTAtertiaryWithAniIcon,
  CTAlinkInline,
} from './skins/CTA.tertiary';
import { HoverLineHoverAfter } from './CTASkins';
import { mq } from '~/styles/grid';
import { renderCTAIcon } from './CTA.helpers';

const CTAWrapper = styled.div`
  display: inline-block;

  ${({ $fullWidth }) =>
    $fullWidth &&
    css`
      width: 100%;
    `}

  ${({ $isHovered }) =>
    $isHovered &&
    css`
      .arrowAnimated svg {
        transform: translateX(6px);
      }
      @media (${mq.mdUp}) {
        .arrowAnimated svg {
          transform: translateX(6px);
        }
        .text::after {
          ${HoverLineHoverAfter};
        }

        .text::after,
        .iconBefore:hover ~ .text::after,
        .iconAfter:hover ~ .text::after {
          ${HoverLineHoverAfter};
        }
      }
    `}

  @media (${(props) => props.theme.teaserCareer.mobileSwitch}) {
    margin: 0;
  }
`;

export const StyledLink = styled.a`
  text-decoration: none;
`;

export const StyledButton = styled.button`
  cursor: pointer;
`;

/**
 * @type {CtaSkinMapType}
 */
export const ctaSkinMap = {
  PRIMARY: CTAprimary,
  SECONDARY: CTAsecondary,
  TERTIARY: CTAtertiaryWithIcon,
  LINK: CTAtertiaryWithIcon,
  LINK_INLINE: CTAlinkInline,
  NAVIGATION_HEADER: CTAnavigationHeader,
  NAVIGATION_ITEM: CTAnavigationItem,
  NAVIGATION_FOOTER: CTAnavigationFooter,
  ICON_ONLY: CTAiconOnly,
};

/**
 * @type {AnimatedCtaSkinType}
 */
export const ctaSkinAnimatedMap = {
  PRIMARY: CTAprimaryWithAniIcon,
  SECONDARY: CTAsecondaryWithAniIcon,
  TERTIARY: CTAtertiaryWithAniIcon,
  NAVIGATION_HEADER: CTAnavigationWithAniIcon,
  NAVIGATION_ITEM: CTAnavigationItem,
  NAVIGATION_FOOTER: CTAnavigationWithAniIcon,
  ICON_ONLY: CTAiconOnly,
};

/**
 * @type {TagMapType}
 */
const tagMap = {
  Default: StyledLink,
  Button: StyledButton,
  Link: StyledLink,
};

/**
 * @param {CTAProps} props
 * @returns {React.ReactElement} The rendered component
 */
const CTA = ({
  className,
  type,
  disabled,
  skin,
  icon,
  iconPos,
  text,
  title,
  handleBtnClick,
  href,
  url = href,
  target,
  isBrand,
  fullWidth,
  parentIsHovered,
  rotation,
  iconAfter,
  iconBefore,
  height,
  width,
  ariaLabel,
  size = 'DEFAULT',
}) => {
  const [hovered, isHover] = useState(false);
  const rel = createLinkRel(target);
  const isArrowAnimated = ['ARROW_ANIMATION'].includes(skin);
  const isNavigation = ['NAVIGATION_HEADER', 'NAVIGATION_FOOTER'].includes(
    skin,
  );
  const isLink = ['LINK', 'LINK_INLINE'].includes(skin);

  let SkinnedCTA;
  if (isArrowAnimated && !isLink) {
    SkinnedCTA = ctaSkinAnimatedMap[skin];
  } else {
    SkinnedCTA = ctaSkinMap[skin];
  }

  const createUrl = React.useContext(UrlContext);
  // Support rotation as number (applies to both icons) or [before, after]
  const rotationBefore = Array.isArray(rotation) ? rotation[0] : rotation;
  const rotationAfter = Array.isArray(rotation) ? rotation[1] : rotation;
  const uri = href || url;
  const defaultType = uri ? CTATag.Default : CTATag.Button;

  return (
    <CTAWrapper
      className={className}
      onMouseEnter={() => isHover(true)}
      onMouseLeave={() => isHover(false)}
      data-id="cta-wrapper"
      $fullWidth={fullWidth}
      $isHovered={parentIsHovered || hovered}
    >
      <SkinnedCTA
        data-id={`skinned-cta-${skin}`}
        aria-label={ariaLabel || text}
        // @ts-ignore
        as={type ? tagMap[type] : tagMap[defaultType]}
        $fullWidth={fullWidth}
        {...{
          isBrand,
          title,
          text,
          disabled,
          href: uri ? createUrl(uri) : undefined,
          target,
          onClick: handleBtnClick,
          rel,
          size,
        }}
      >
        {(iconBefore || (icon && iconPos === CTAIconPosition.Before)) &&
          renderCTAIcon({
            hovered,
            skin,
            isArrowAnimated,
            isNavigation,
            rotation: rotationBefore,
            iconName: iconBefore ?? icon,
            position: 'Before',
            height,
            width,
          })}
        {text && <span className="text">{text}</span>}
        {(iconAfter || (icon && iconPos === CTAIconPosition.After)) &&
          renderCTAIcon({
            hovered,
            skin,
            isArrowAnimated,
            isNavigation,
            rotation: rotationAfter,
            iconName: iconAfter ?? icon,
            position: 'After',
            height,
            width,
          })}
      </SkinnedCTA>
    </CTAWrapper>
  );
};

export default CTA;
